import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Bell, User, LogOut, Crown, Search, Menu, X, FolderTree, ChevronDown, Lock, Calculator } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { API } from '../lib/api';
import { LanguageSwitcher } from './LanguageSwitcher';

export function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showCategoriesMenu, setShowCategoriesMenu] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    axios.get(`${API}/categories`)
      .then((res) => setCategories(res.data.filter((c) => (c.item_count || 0) > 0)))
      .catch(() => setCategories([]));
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
    }
  };

  const showPremiumToast = () => toast.info(t('common.premium_soon_toast'), { duration: 4000 });

  return (
    <nav data-testid="main-navbar" className="fixed top-0 w-full z-50 backdrop-blur-xl bg-[#0A0A0A]/80 border-b border-white/5 transition-all duration-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center space-x-3" data-testid="logo-link">
            <div className="w-10 h-10 bg-gradient-to-r from-[#0066FF] to-[#00FF99] rounded-lg flex items-center justify-center">
              <span className="text-black font-black text-xl">BV</span>
            </div>
            <span className="text-xl font-black tracking-tight text-white">{t('common.brand')}</span>
          </Link>

          {categories.length > 0 && (
            <div className="hidden md:block relative">
              <button
                data-testid="categories-menu-button"
                onClick={() => setShowCategoriesMenu(!showCategoriesMenu)}
                onBlur={() => setTimeout(() => setShowCategoriesMenu(false), 150)}
                className="flex items-center space-x-2 text-white hover:text-[#00FF99] transition-colors px-3 py-2 rounded-lg"
              >
                <FolderTree className="w-4 h-4" />
                <span className="font-semibold text-sm">{t('nav.categories')}</span>
                <ChevronDown className={`w-4 h-4 transition-transform ${showCategoriesMenu ? 'rotate-180' : ''}`} />
              </button>
              <AnimatePresence>
                {showCategoriesMenu && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    data-testid="categories-dropdown"
                    className="absolute left-0 mt-2 w-64 backdrop-blur-xl bg-[#121212]/95 border border-white/10 rounded-lg shadow-xl overflow-hidden z-50 max-h-[70vh] overflow-y-auto"
                  >
                    <div className="p-2">
                      {categories.map((cat) => (
                        <Link
                          key={cat.id}
                          to={`/category/${cat.id}`}
                          data-testid={`category-link-${cat.id}`}
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => setShowCategoriesMenu(false)}
                          className="flex items-center justify-between px-3 py-2.5 text-white hover:bg-[#1A1A1A] rounded transition-colors group"
                        >
                          <div className="flex items-center space-x-2 min-w-0">
                            {cat.premium && <Lock className="w-3.5 h-3.5 text-[#00FF99] shrink-0" />}
                            <span className="text-sm font-semibold truncate group-hover:text-[#00FF99] transition-colors">
                              {cat.name}
                            </span>
                          </div>
                          <span className="text-xs text-zinc-500 font-mono shrink-0 ml-2">{cat.item_count}</span>
                        </Link>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}

          <Link
            to="/calculator"
            data-testid="calculator-nav-link"
            className="hidden md:flex items-center space-x-2 text-white hover:text-[#00FF99] transition-colors px-3 py-2 rounded-lg"
          >
            <Calculator className="w-4 h-4" />
            <span className="font-semibold text-sm">{t('nav.calculator')}</span>
          </Link>

          <form onSubmit={handleSearch} className="hidden md:flex flex-1 max-w-xl mx-8">
            <div className="relative w-full">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-400" />
              <input
                type="text"
                data-testid="search-input"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t('nav.search_placeholder')}
                className="w-full bg-[#1A1A1A] border border-[#27272A] rounded-lg pl-12 pr-4 py-2.5 text-white placeholder-zinc-500 focus:outline-none focus:border-[#0066FF] transition-colors"
              />
            </div>
          </form>

          <div className="hidden md:flex items-center space-x-3">
            <LanguageSwitcher />
            {user ? (
              <>
                {!user.is_premium && (
                  <button
                    data-testid="upgrade-premium-button"
                    onClick={showPremiumToast}
                    className="flex items-center space-x-2 bg-gradient-to-r from-[#0066FF] to-[#00FF99] text-black px-4 py-2 rounded-lg font-bold hover:opacity-90 transition-all"
                  >
                    <Crown className="w-4 h-4" />
                    <span>{t('nav.upgrade')}</span>
                  </button>
                )}
                {user.is_premium && (
                  <div className="flex items-center space-x-2 bg-[#1A1A1A] border border-[#00FF99] text-[#00FF99] px-4 py-2 rounded-lg">
                    <Crown className="w-4 h-4" />
                    <span className="font-bold text-sm">{t('nav.premium_badge')}</span>
                  </div>
                )}
                <Link to="/favorites" data-testid="favorites-link" className="text-white hover:text-[#00FF99] transition-colors">
                  <Bell className="w-5 h-5" />
                </Link>
                <div className="relative">
                  <button
                    data-testid="user-menu-button"
                    onClick={() => setShowUserMenu(!showUserMenu)}
                    className="flex items-center space-x-2 text-white hover:text-[#0066FF] transition-colors"
                  >
                    <div className="w-9 h-9 bg-[#1A1A1A] rounded-full flex items-center justify-center border border-[#27272A]">
                      <User className="w-5 h-5" />
                    </div>
                  </button>
                  <AnimatePresence>
                    {showUserMenu && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        data-testid="user-dropdown"
                        className="absolute right-0 mt-2 w-56 backdrop-blur-xl bg-[#121212]/90 border border-white/10 rounded-lg shadow-xl overflow-hidden"
                      >
                        <div className="p-4 border-b border-white/5">
                          <p className="text-white font-semibold">{user.name}</p>
                          <p className="text-zinc-400 text-sm">{user.email}</p>
                        </div>
                        <div className="p-2">
                          {user.role === 'admin' && (
                            <Link
                              to="/admin"
                              data-testid="admin-link"
                              className="flex items-center space-x-3 px-4 py-2 text-white hover:bg-[#1A1A1A] rounded transition-colors"
                              onClick={() => setShowUserMenu(false)}
                            >
                              <Crown className="w-4 h-4" />
                              <span>{t('nav.admin_panel')}</span>
                            </Link>
                          )}
                          <button
                            data-testid="logout-button"
                            onClick={handleLogout}
                            className="w-full flex items-center space-x-3 px-4 py-2 text-red-400 hover:bg-[#1A1A1A] rounded transition-colors"
                          >
                            <LogOut className="w-4 h-4" />
                            <span>{t('nav.logout')}</span>
                          </button>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </>
            ) : (
              <Link
                to="/login"
                data-testid="login-button"
                className="bg-[#0066FF] text-white px-6 py-2 rounded-lg font-bold hover:bg-[#3385FF] transition-all"
              >
                {t('nav.login')}
              </Link>
            )}
          </div>

          <button
            data-testid="mobile-menu-button"
            onClick={() => setShowMobileMenu(!showMobileMenu)}
            className="md:hidden text-white"
          >
            {showMobileMenu ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {showMobileMenu && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden backdrop-blur-xl bg-[#0A0A0A]/95 border-t border-white/5"
          >
            <div className="px-4 py-4 space-y-3">
              <form onSubmit={handleSearch} className="mb-4">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={t('nav.search_placeholder_short')}
                  className="w-full bg-[#1A1A1A] border border-[#27272A] rounded-lg px-4 py-2.5 text-white"
                />
              </form>
              <LanguageSwitcher variant="mobile" />
              <Link
                to="/calculator"
                data-testid="calculator-nav-link-mobile"
                onClick={() => setShowMobileMenu(false)}
                className="flex items-center space-x-2 text-white hover:bg-[#1A1A1A] px-3 py-2 rounded text-sm font-semibold"
              >
                <Calculator className="w-4 h-4 text-[#00FF99]" />
                <span>{t('nav.calculator_full')}</span>
              </Link>
              {categories.length > 0 && (
                <div className="mb-3">
                  <div className="text-xs text-zinc-500 uppercase font-bold mb-2 px-1">{t('nav.categories')}</div>
                  <div className="space-y-1 max-h-64 overflow-y-auto">
                    {categories.map((cat) => (
                      <Link
                        key={cat.id}
                        to={`/category/${cat.id}`}
                        onClick={() => setShowMobileMenu(false)}
                        className="flex items-center justify-between px-3 py-2 text-white hover:bg-[#1A1A1A] rounded text-sm"
                      >
                        <span className="flex items-center space-x-2">
                          {cat.premium && <Lock className="w-3 h-3 text-[#00FF99]" />}
                          <span>{cat.name}</span>
                        </span>
                        <span className="text-xs text-zinc-500 font-mono">{cat.item_count}</span>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
              {user && !user.is_premium && (
                <button
                  onClick={() => { setShowMobileMenu(false); showPremiumToast(); }}
                  className="w-full flex items-center space-x-2 bg-gradient-to-r from-[#0066FF] to-[#00FF99] text-black px-4 py-2 rounded-lg font-bold"
                >
                  <Crown className="w-4 h-4" />
                  <span>{t('nav.upgrade_mobile')}</span>
                </button>
              )}
              {user && user.role === 'admin' && (
                <Link to="/admin" className="block text-white px-4 py-2 hover:bg-[#1A1A1A] rounded">
                  {t('nav.admin_panel')}
                </Link>
              )}
              {user ? (
                <button
                  onClick={handleLogout}
                  className="w-full text-left text-red-400 px-4 py-2 hover:bg-[#1A1A1A] rounded"
                >
                  {t('nav.logout')}
                </button>
              ) : (
                <Link to="/login" className="block text-white px-4 py-2 bg-[#0066FF] rounded font-bold text-center">
                  {t('nav.login')}
                </Link>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}
